import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { trpc } from "@/hooks/trpc";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

import type { HarvestOperationTemplateRevisionId } from "@brewbase/db/types";

interface UpstreamPickerProps {
  templateId: HarvestOperationTemplateRevisionId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Codes already in the allowlist — shown as disabled rows. */
  existingCodes: Set<string>;
}

export function UpstreamPicker({
  templateId,
  open,
  onOpenChange,
  existingCodes,
}: UpstreamPickerProps) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const utils = trpc.useUtils();
  const { data: results = [], isLoading } = trpc.harvest.attributeCodes.search.useQuery(
    { query: query || undefined, limit: 200 },
    { enabled: open },
  );
  const add = trpc.harvest.templateUpstreamDeps.add.useMutation({
    onSuccess: () =>
      Promise.all([
        utils.harvest.templateUpstreamDeps.list.invalidate({
          operation_template_id: templateId,
        }),
        // The formula context (used for diagnostics) embeds the upstream
        // allowlist server-side — invalidate it too or the editor keeps
        // flagging the freshly added code as undeclared.
        utils.formulas.getContext.invalidate(),
      ]),
  });

  const grouped = useMemo(() => {
    const byProducer = new Map<string, typeof results>();
    const unowned: typeof results = [];
    for (const r of results) {
      if (r.producers.length === 0) {
        unowned.push(r);
        continue;
      }
      for (const p of r.producers) {
        const key = p.templateName;
        const list = byProducer.get(key) ?? [];
        list.push(r);
        byProducer.set(key, list);
      }
    }
    return { byProducer: Array.from(byProducer.entries()).sort(([a], [b]) => a.localeCompare(b)), unowned };
  }, [results]);

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function handleConfirm() {
    const codes = Array.from(selected);
    if (codes.length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      await Promise.all(
        codes.map((code) =>
          add.mutateAsync({ operation_template_id: templateId, code }),
        ),
      );
      toast.success(`Added ${codes.length} upstream dependency${codes.length > 1 ? "ies" : ""}`);
      setSelected(new Set());
      setQuery("");
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add dependencies");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add upstream dependency</DialogTitle>
        </DialogHeader>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search codes or descriptions…"
          autoFocus
        />
        <div className="max-h-96 overflow-y-auto border border-olive-6 divide-y divide-olive-4">
          {isLoading && (
            <div className="p-3 text-sm text-olive-10">Loading…</div>
          )}
          {!isLoading && results.length === 0 && (
            <div className="p-3 text-sm text-olive-10">No codes found.</div>
          )}
          {grouped.byProducer.map(([producer, items]) => (
            <div key={producer}>
              <div className="px-2 py-1 text-xs uppercase tracking-wide text-olive-10 bg-olive-3">
                {producer}
              </div>
              {items.map((r) => (
                <PickerRow
                  key={`${producer}/${r.code}`}
                  code={r.code}
                  description={r.description}
                  disabled={existingCodes.has(r.code)}
                  selected={selected.has(r.code)}
                  onToggle={() => toggle(r.code)}
                />
              ))}
            </div>
          ))}
          {grouped.unowned.length > 0 && (
            <div>
              <div className="px-2 py-1 text-xs uppercase tracking-wide text-olive-10 bg-olive-3">
                Unassigned
              </div>
              {grouped.unowned.map((r) => (
                <PickerRow
                  key={`_unowned/${r.code}`}
                  code={r.code}
                  description={r.description}
                  disabled={existingCodes.has(r.code)}
                  selected={selected.has(r.code)}
                  onToggle={() => toggle(r.code)}
                />
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="default"
            disabled={selected.size === 0 || add.isPending}
            onClick={handleConfirm}
          >
            Add {selected.size > 0 ? `(${selected.size})` : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PickerRow({
  code,
  description,
  disabled,
  selected,
  onToggle,
}: {
  code: string;
  description: string | null;
  disabled: boolean;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        "w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-olive-3",
        selected && "bg-olive-4",
        disabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
      )}
    >
      <span
        className={cn(
          "w-3 h-3 border inline-block flex-none",
          selected ? "bg-[var(--farmless-green)] border-[var(--farmless-green)]" : "border-olive-8",
        )}
      />
      <span className="font-mono text-sm flex-1">{code}</span>
      {disabled && (
        <span className="text-xs text-olive-10">already added</span>
      )}
      {description && !disabled && (
        <span className="text-xs text-olive-10 truncate max-w-[50%]">
          {description}
        </span>
      )}
    </button>
  );
}
